import React, { useEffect, useMemo, useRef } from "react";

import {
  faFilter,
  faTrashCan,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { faFilter as faFilterSolid } from "@fortawesome/pro-solid-svg-icons";
import { FormProvider, useForm } from "react-hook-form";

import { Button } from "src/base-components/Button";
import { DEFAULT_FILTER_FORM_VALUES } from "src/base-components/FilterPopover/constants";
import { FilterGroups } from "src/base-components/FilterPopover/FilterGroups";
import {
  FilterFormData,
  FilterPopoverProps,
} from "src/base-components/FilterPopover/types";
import { isFieldDefinition } from "src/base-components/FilterPopover/utils";
import { createFilterResolver } from "src/base-components/FilterPopover/validation";
import { CustomPopover } from "src/base-components/Popover";
import { Icon } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import { SubHeaderButton } from "src/layout/SubHeader";

export const FilterPopover: React.FC<FilterPopoverProps> = ({
  dataLoc,
  isOpen,
  onOpenChange,
  onApply,
  fields,
  sections,
  valueRenderers,
  operatorOptions,
  fieldOverrides,
  validateValue,
  renderHeader,
  defaultValues,
  hasActiveFilter = false,
  isLoading = false,
  maxGroups,
  maxConditionsPerGroup,
  allowedJunctions,
  button: customButton,
}) => {
  const resolver = useMemo(() => {
    return createFilterResolver({
      fields: fields.filter(isFieldDefinition),
      validateValue,
    });
  }, [fields, validateValue]);

  const form = useForm<FilterFormData>({
    resolver,
    defaultValues: defaultValues ?? DEFAULT_FILTER_FORM_VALUES,
    mode: "onChange",
  });

  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues);
    } else if (!hasActiveFilter) {
      form.reset(DEFAULT_FILTER_FORM_VALUES);
    }
  }, [hasActiveFilter, defaultValues, form]);

  const handleApply = () => {
    const values = form.getValues();
    onOpenChange(false);
    onApply(values);
  };

  const handleClear = () => {
    form.reset(DEFAULT_FILTER_FORM_VALUES);
  };

  const isFilterApplied = isOpen || hasActiveFilter;
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.closest("[data-headlessui-portal]") ||
        containerRef.current?.contains(target)
      )
        return;
      onOpenChange(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [isOpen, onOpenChange]);

  const defaultButton = (
    <SubHeaderButton
      icon={isFilterApplied ? faFilterSolid : faFilter}
      tooltip="All filters"
      tooltipPlacement="right"
      variant={isFilterApplied ? "active" : "default"}
      onClick={() => onOpenChange(!isOpen)}
    />
  );

  return (
    <div ref={containerRef} className="contents">
      <CustomPopover
        button={customButton ?? defaultButton}
        className="w-[700px]"
        dataLoc={dataLoc}
        isOpen={isOpen}
      >
        <div className="divide-y divide-gray-200">
          <div className="flex items-center justify-between gap-x-3 px-4 pt-1 pb-3">
            <div className="flex items-center gap-x-3 font-inter-semibold-13px text-gray-800">
              {renderHeader}
            </div>
            <CustomPopover.Button
              data-loc={`${dataLoc}-close-button`}
              onClick={() => onOpenChange(false)}
            >
              <Icon icon={faXmark} size="xs" />
            </CustomPopover.Button>
          </div>

          <div className="p-4">
            {isLoading ? (
              <div
                className="flex items-center gap-x-2"
                data-loc={`${dataLoc}-loading`}
              >
                <Skeleton height="h-8" width="w-46" />
                <Skeleton height="h-8" width="w-32" />
                <Skeleton height="h-8" />
              </div>
            ) : (
              <FormProvider {...form}>
                <FilterGroups
                  allowedJunctions={allowedJunctions}
                  fieldOverrides={fieldOverrides}
                  fields={fields}
                  maxConditionsPerGroup={maxConditionsPerGroup}
                  maxGroups={maxGroups}
                  operatorOptions={operatorOptions}
                  sections={sections}
                  valueRenderers={valueRenderers}
                />
              </FormProvider>
            )}
          </div>

          <div className="flex justify-between px-4 pt-4 pb-2">
            <CustomPopover.Button
              as={Button}
              dataLoc={`${dataLoc}-clear-button`}
              disabled={isLoading}
              iconLeft={faTrashCan}
              size="sm"
              variant="secondary"
              onClick={handleClear}
            >
              Clear filters
            </CustomPopover.Button>
            <CustomPopover.Button
              as={Button}
              dataLoc={`${dataLoc}-apply-button`}
              disabled={isLoading || !form.formState.isValid}
              size="sm"
              variant="primary"
              onClick={handleApply}
            >
              Apply
            </CustomPopover.Button>
          </div>
        </div>
      </CustomPopover>
    </div>
  );
};
